import ExportIcon from '@mui/icons-material/Description'
import React from 'react'
import { Trans } from 'react-i18next'
import { i18n } from '../index/index'
import {
  AdditionalLegend,
  ExportLink,
  IncentiveSalesProgress,
  Widget,
  WidgetContent,
  WidgetHeader,
} from './OldDashboardComponents'
import css from './incentiveSales.css'

type Incentives = {
  date: string
  euroActual: number
  euroTarget: number
}

type Props = {
  actual: number
  target: number
  date: string
  weeklyIncentiveSales: Incentives[]
  monthlyIncentiveSales: Incentives[]
}

const IncentiveSales = (props: Props) => {
  const { actual, target, date, weeklyIncentiveSales, monthlyIncentiveSales } = props

  // Get the weekly incentive sales until the selected date
  const weeklyIncentiveSalesSoFar = weeklyIncentiveSales.filter(
    (incentive) => new Date(incentive.date) <= new Date(date),
  )

  // Sum of the actual sales so far during the current selected week
  const actualSumSoFar = weeklyIncentiveSalesSoFar.reduce((acc, current) => acc + current.euroActual, 0)
  // Sum of the whole week sales and target
  const weeklyIncentiveSums = weeklyIncentiveSales.reduce(
    (prev, cur) => {
      return {
        actual: prev.actual + cur.euroActual,
        target: prev.target + cur.euroTarget,
      }
    },
    { actual: 0, target: 0 },
  )

  const monthlyIncentiveSums = monthlyIncentiveSales.reduce(
    (prev, cur) => {
      return {
        actual: prev.actual + cur.euroActual,
        target: prev.target + cur.euroTarget,
      }
    },
    { actual: 0, target: 0 },
  )

  const monthlyIncentivesSuccessRate =
    monthlyIncentiveSums.actual > 0 && monthlyIncentiveSums.target > 0
      ? ((monthlyIncentiveSums.actual / monthlyIncentiveSums.target) * 100).toFixed(1)
      : '-'

  // Viikkoerotus
  const sumOfWeeklyTarget = Math.round(actualSumSoFar - weeklyIncentiveSums.target)

  const targetPercentOfWeeklyTarget =
    weeklyIncentiveSums.target > 0.0 ? Math.round((sumOfWeeklyTarget / weeklyIncentiveSums.target) * 100) : 0.0

  const targetPercentClassName =
    !Number.isNaN(targetPercentOfWeeklyTarget) && targetPercentOfWeeklyTarget !== 0
      ? targetPercentOfWeeklyTarget >= 0
        ? css.valuePositive
        : css.valueNegative
      : undefined

  const successRateClassName = !Number.isNaN(+monthlyIncentivesSuccessRate)
    ? +monthlyIncentivesSuccessRate >= 100
      ? css.valuePositive
      : css.valueNegative
    : undefined

  const sumClassName =
    sumOfWeeklyTarget !== 0 ? (sumOfWeeklyTarget >= 0 ? css.valuePositive : css.valueNegative) : undefined

  const sumOfWeeklyTargetTranslationKey = !weeklyIncentiveSums.target
    ? 'oldDashboard.widget.incentiveSales.targetMissing'
    : 'oldDashboard.widget.incentiveSales.percentOfTarget'

  return (
    <Widget>
      <WidgetHeader>{i18n.t('oldDashboard.widget.incentiveSales.title')}</WidgetHeader>
      <WidgetContent>
        <IncentiveSalesProgress
          value={target ? Math.round((actual / target) * 100) : '-'}
          legend={
            <Trans
              i18nKey='oldDashboard.widget.incentiveSales.legend'
              values={{ target: Math.round(target), actual: Math.round(actual) }}
              components={{ actual: <strong />, target: <strong /> }}
            />
          }
        />
        <AdditionalLegend>
          <Trans
            i18nKey={sumOfWeeklyTargetTranslationKey}
            values={{ sum: Math.abs(sumOfWeeklyTarget), targetPercent: Math.abs(targetPercentOfWeeklyTarget) || '-' }}
            components={{
              sum: <strong className={sumClassName} />,
              targetPercent: <strong className={targetPercentClassName} />,
            }}
          />
        </AdditionalLegend>
        <AdditionalLegend>
          <Trans
            i18nKey='oldDashboard.widget.incentiveSales.successRate'
            values={{ successRate: monthlyIncentivesSuccessRate }}
            components={{ successRate: <strong className={successRateClassName} /> }}
          />
        </AdditionalLegend>
      </WidgetContent>
      <ExportLink to={`/vuorokausijohtaminen/${date}#miehistosunnitelma`}>
        <ExportIcon />
      </ExportLink>
    </Widget>
  )
}

export default IncentiveSales
